import type { DocPageContent } from '../docs-content'

const page = {
  order: 3,
  slug: 'projects',
  title: '项目',
  description: '创建、配置、复制、整理、删除、恢复以及移动项目。',
  category: '入门',
  related: ['workspaces', 'editor'],
  sections: [
    {
      title: '创建并配置项目',
      blocks: [
        {
          kind: 'list',
          items: [
            '在“项目”页面通过**新建项目**创建一个项目。',
            '设置项目名称、描述、分辨率、帧率和背景颜色。',
            '分辨率和帧率决定了导出画布和时间轴时序，因此请根据你的目标交付规格来选择。',
            '稍后你可以在编辑器工具栏中重新查看项目规格。',
          ],
        },
      ],
    },
    {
      title: '画布尺寸与宽高比',
      blocks: [
        {
          kind: 'paragraph',
          text: '画布定义了视频合成与导出的帧。在创建项目时选择尺寸，或在编辑时通过**画布**面板随时更改。',
        },
        {
          kind: 'paragraph',
          text: '在**新建项目**时，**分辨率**部分提供适用于常见平台的现成预设。每个预设卡片都显示其像素尺寸与宽高比。选择**自定义**可自行输入精确的宽度和高度。',
        },
        {
          kind: 'table',
          headers: ['预设', '分辨率', '宽高比'],
          rows: [
            ['YouTube 1080p', '`1920x1080`', '`16:9` 横屏'],
            ['Shorts / TikTok / Reels', '`1080x1920`', '`9:16` 竖屏'],
            ['Instagram Square', '`1080x1080`', '`1:1` 方形'],
            ['Instagram Portrait', '`1080x1350`', '`4:5` 竖幅'],
            ['Twitter/X', '`1200x675`', '`16:9` 横屏'],
            ['LinkedIn', '`1200x627`', '横屏'],
            ['Custom', '你自行设定的值', '你输入的任意值'],
          ],
        },
        {
          kind: 'paragraph',
          text: '要在项目创建后更改尺寸：',
        },
        {
          kind: 'steps',
          items: [
            '取消选中任何片段，使属性侧边栏显示**画布**面板。',
            '编辑**W**和**H**字段，以像素为单位设置帧的宽度和高度。',
            '使用**交换**翻转宽度与高度，例如将横屏项目转为竖屏。',
            '使用**重置**将画布恢复为 `1920x1080`。',
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: '尺寸会吸附到偶数，范围从 `320x240` 到 `7680x4320`。画布改变时，已有片段会保持当前位置和缩放，因此请重新定位那些现在落在新帧之外的片段。',
        },
      ],
    },
    {
      title: '整理项目列表',
      blocks: [
        {
          kind: 'list',
          items: [
            '按名称搜索项目，并在列表增长时对其进行排序或筛选。',
            '使用缩略图和元数据来识别你上次处理的项目。',
            '“项目”页面会显示当前活动的工作区，因此你始终清楚项目存储在何处。',
            '使用**复制**在不改动原项目的情况下派生一个变体。',
          ],
        },
      ],
    },
    {
      title: '删除与恢复',
      blocks: [
        {
          kind: 'list',
          items: [
            '删除项目会将其移入**回收站**，而不是立即移除。',
            '在回收站中仍可恢复项目时，从回收站部分将其恢复。',
            '**清空回收站**会永久删除被移入回收站的项目，以及它们独占引用的任何媒体。',
          ],
        },
        {
          kind: 'note',
          tone: 'warning',
          text: '永久删除无法撤销，因此请在清空回收站前确认名称。',
        },
      ],
    },
    {
      title: '在工作区之间移动工作成果',
      blocks: [
        {
          kind: 'list',
          items: [
            '使用**导出项目**创建一个将项目与其媒体打包在一起的包。',
            '将包导入另一个工作区，以便在不同机器或文件夹上继续同一剪辑。',
          ],
        },
        {
          kind: 'note',
          tone: 'tip',
          text: '打包是把手头项目交给他人的安全方式，因为它们会将链接的媒体与时间轴一起携带。',
        },
      ],
    },
  ],
} satisfies DocPageContent

export default page
